import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import axios from "axios";
import ordersService from "../services/ordersService";

interface CitySummary {
  city: string;
  totalOrders: number;
  totalRecords: number;
}

interface MonthlySummary {
  month: string;
  totalOrders: number;
}

interface AnalyticsState {
  citySummary: CitySummary[];
  monthlySummary: MonthlySummary[];
  loading: boolean;
  error: string | null;
}

const GATEWAY_URL =
  import.meta.env.VITE_RIDER_API_URL || "http://localhost:8000";

const METRIC_COLORS = {
  primary: "#1976d2",
  success: "#4caf50",
  warning: "#ff9800",
  error: "#f44336",
};

const CLIENT_PALETTE = [
  "#4caf50",
  "#ff9800",
  "#f44336",
  "#1976d2",
  "#9c27b0",
  "#00acc1",
  "#ffb300",
  "#c0ca33",
];

// how many months to display in charts
const MONTHS_TO_SHOW = 6;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const OrderAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsState>({
    citySummary: [],
    monthlySummary: [],
    loading: false,
    error: null,
  });

  const [dateFrom, setDateFrom] = useState("");
  
  const [dateTo, setDateTo] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [cityMonthlyAnalytics, setCityMonthlyAnalytics] = useState<MonthlySummary[]>([]);
  const [cityClientMonthlyData, setCityClientMonthlyData] = useState<any[]>([]);
  const [cityClientSeries, setCityClientSeries] = useState<string[]>([]);
  const [clientColorMap, setClientColorMap] = useState<Record<string, string>>({});
  const [_clientAnalytics, setClientAnalytics] = useState<Array<{ clientName?: string; totalOrders: number }>>([]);

  const { enqueueSnackbar } = useSnackbar();

  // Fetch city-wise summary
  const fetchCitySummary = async () => {
    setAnalyticsData((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${GATEWAY_URL}/orders/city/summary?${params.toString()}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      const result = response.data.summary || [];

      setAnalyticsData((prev) => ({
        ...prev,
        citySummary: result,
        loading: false,
        error: null,
      }));


      // enqueueSnackbar("City analytics loaded successfully", {
      //   variant: "success",
      // });
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch city analytics";
      setAnalyticsData((prev) => ({
        ...prev,
        loading: false,
        error: errorMsg,
      }));
      enqueueSnackbar(errorMsg, { variant: "error" });
    }
  };


  const formatMonthDisplay = (monthKey: string) => {
    const [year, month] = monthKey.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short" });
  };

  // Deterministic color generation for arbitrary client names
  const hashStringToHue = (s: string) => {
    let hash = 0;
    for (let i = 0; i < s.length; i++) {
      hash = (hash << 5) - hash + s.charCodeAt(i);
      hash |= 0; // convert to 32bit int
    }
    return Math.abs(hash) % 360;
  };

  const getColorFromName = (name: string) => {
    const hue = hashStringToHue(name || "");
    return `hsl(${hue}, 68%, 42%)`;
  };

  // Custom tooltip for Monthly Client-wise Orders: show only clients with >0 values
  const ClientStackedTooltip: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const filtered = payload.filter((p: any) => Number(p.value) > 0);
    if (filtered.length === 0) return null;
    return (
      <Box sx={{ bgcolor: 'background.paper', p: 1, borderRadius: 1, boxShadow: 2 }}>
        <Typography variant="subtitle2">{label}</Typography>
        {filtered.map((p: any) => (
          <Box key={p.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Box sx={{ width: 12, height: 12, backgroundColor: p.stroke || p.fill, borderRadius: '2px' }} />
            <Typography sx={{ fontSize: 13 }}>
              {p.name}: {Number(p.value).toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };



  // Fetch client-wise analytics for a city (aggregates by clientId/clientName)
  const fetchCityClientAnalytics = async (city: string) => {
    try {
      const resp = await ordersService.getClientWiseByCityByName({ dateFrom, dateTo, city });
      const data = resp?.byCity || resp || [];
      const entry = (data as any[]).find((e) => String(e.city).toLowerCase() === String(city).toLowerCase() || String(e.city) === city);
      if (!entry || !entry.clients) {
        setClientAnalytics([]);
        return;
      }
      const clientsMap: Record<string, number> = entry.clients || {};
      const arr = Object.entries(clientsMap).map(([clientName, total]) => ({ clientName, totalOrders: Number(total || 0) }));
      const sorted = arr.sort((a, b) => b.totalOrders - a.totalOrders);
      setClientAnalytics(sorted);
    } catch (err: any) {
      console.error("Failed to fetch city client analytics", err);
      setClientAnalytics([]);
    }
  };

  // Fetch monthly client-wise data for a city (months -> clients -> totals)
  const fetchCityMonthlyClientAnalytics = async (city: string) => {
    try {
      const resp = await ordersService.getMonthlyClientCitySummary({ dateFrom, dateTo, city });
      const arr = resp || [];

      // find the city entry. Controller normalizes keys to lowercase for city
      const entry = (Array.isArray(arr) ? arr : [])
        .find((e: any) => String(e.city).toLowerCase() === String(city).toLowerCase() || String(e.city) === city);
      if (!entry || !entry.months) {
        setCityClientMonthlyData([]);
        setCityClientSeries([]);
        return;
      }

      const monthsObj: Record<string, Record<string, number>> = entry.months;
      const monthsKeys = Object.keys(monthsObj).sort();
      const monthsToDisplay = monthsKeys.slice(-MONTHS_TO_SHOW);

      // Collect client totals across months to pick top N
      const totalsMap: Record<string, number> = {};
      for (const m of monthsToDisplay) {
        const clients = monthsObj[m] || {};
        for (const [client, val] of Object.entries(clients)) {
          totalsMap[client] = (totalsMap[client] || 0) + Number(val || 0);
        }
      }

      const sortedClients = Object.entries(totalsMap)
        .sort((a, b) => b[1] - a[1])
        .map((c) => c[0]);

      const TOP_N = 6;
      const topClients = sortedClients.slice(0, TOP_N);
      const includeOther = sortedClients.length > TOP_N;

      // Build chart data rows: { month: 'Jan 2025', 'Client A': 10, ... 'Other': 5 }
      const chartData: any[] = monthsToDisplay.map((m) => {
        const clients = monthsObj[m] || {};
        const row: any = { month: formatMonthDisplay(m) };
        let otherTotal = 0;
        for (const [client, val] of Object.entries(clients)) {
          const count = Number(val || 0);
          if (topClients.includes(client)) {
            row[client] = count;
          } else {
            otherTotal += count;
          }
        }
        if (includeOther) row.Other = otherTotal;
        // Ensure top clients have zero when missing
        for (const c of topClients) if (!row[c]) row[c] = 0;
        if (includeOther && !row.Other) row.Other = 0;
        return row;
      });

      setCityClientMonthlyData(chartData);
      const series = [...topClients];
      if (includeOther) series.push("Other");
      setCityClientSeries(series);
      // Build a stable color mapping for the series
      const mapping: Record<string, string> = {};
      series.forEach((client) => {
        if (client === "Other") mapping[client] = "#9e9e9e"; // gray for other bucket
        else mapping[client] = getColorFromName(client);
      });
      setClientColorMap(mapping);
    } catch (err: any) {
      console.error("Failed to fetch monthly client-wise analytics for city", err);
      setCityClientMonthlyData([]);
      setCityClientSeries([]);
    }
  };

  // Fetch global monthly totals (YYYY-MM) aggregated by date
  const fetchMonthlyOrders = async () => {
    try {
      const response = await ordersService.getOrders({ page: 1, pageSize: 10000, dateFrom, dateTo });
      const orders = response?.data || [];

      const map: Record<string, number> = {};
      for (const o of orders) {
        const month = o.date ? new Date(o.date).toISOString().slice(0, 7) : null;
        if (!month) continue;
        map[month] = (map[month] || 0) + (o.totalOrdersDay || 0);
      }

      const arr = Object.keys(map)
        .sort()
        .map((m) => ({ month: formatMonthDisplay(m), monthKey: m, totalOrders: map[m] }));

      setAnalyticsData((prev) => ({ ...prev, monthlySummary: arr }));
    } catch (err: any) {
      console.error("Failed to fetch monthly orders", err);
      setAnalyticsData((prev) => ({ ...prev, monthlySummary: [] }));
    }
  };

  // Fetch monthly totals for a city (YYYY-MM)
  const fetchCityMonthlyAnalytics = async (city: string) => {
    try {
      const response = await ordersService.getOrdersByCity(city, { page: 1, pageSize: 10000, dateFrom, dateTo });
      const orders = response?.data || [];

      const map: Record<string, number> = {};
      for (const o of orders) {
        const month = o.date ? new Date(o.date).toISOString().slice(0, 7) : null; // YYYY-MM
        if (!month) continue;
        map[month] = (map[month] || 0) + (o.totalOrdersDay || 0);
      }

      const arr = Object.keys(map)
        .sort()
        .map((m) => ({ month: formatMonthDisplay(m), totalOrders: map[m] }));

      setCityMonthlyAnalytics(arr);
    } catch (err: any) {
      console.error("Failed to fetch city monthly analytics", err);
      setCityMonthlyAnalytics([]);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCitySummary();
    fetchMonthlyOrders();
  }, []);

  // Update monthly summaries when filters change
  useEffect(() => {
    // refresh global/monthly data
    fetchMonthlyOrders();
    // If a city is selected, refresh its client and monthly analytics
    if (selectedCity) {
      fetchCityClientAnalytics(selectedCity);
      fetchCityMonthlyAnalytics(selectedCity);
      fetchCityMonthlyClientAnalytics(selectedCity);
    }
  }, [dateFrom, dateTo, analyticsData.citySummary, selectedCity]);

  // Reset filters
  const handleResetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setTabValue(0);
    setSelectedCity(null);
    setClientAnalytics([]);
    setCityMonthlyAnalytics([]);
    setCityClientMonthlyData([]);
    setCityClientSeries([]);
    // Refresh data after resetting filters
    fetchCitySummary();
    fetchMonthlyOrders();
  };

  // Export to CSV
  const monthlyDataToShow = selectedCity ? cityMonthlyAnalytics : analyticsData.monthlySummary;
  // Chart-only datasets (show last N months only)
  const monthlyChartData = (monthlyDataToShow || []).slice(-MONTHS_TO_SHOW);
  const cityMonthlyChartData = (cityMonthlyAnalytics || []).slice(-MONTHS_TO_SHOW);
  const cityClientMonthlyChartData = (cityClientMonthlyData || []).slice(-MONTHS_TO_SHOW);

  const handleExportCSV = () => {
    const data = tabValue === 0 ? analyticsData.citySummary : monthlyDataToShow;

    if (data.length === 0) {
      enqueueSnackbar("No data to export", { variant: "warning" });
      return;
    }

    let csvContent = "";
    let filename = "";

    if (tabValue === 0) {
      const headers = ["City", "Total Orders"];
      csvContent = [
        headers.join(","),
        ...(data as CitySummary[]).map((item) =>
          [item.city, item.totalOrders].join(",")
        ),
      ].join("\n");
      filename = `city-analytics-${Date.now()}.csv`;
    } else {
      const headers = ["Month", "Total Orders"];
      csvContent = [
        headers.join(","),
        ...(data as MonthlySummary[]).map((item) =>
          [item.month, item.totalOrders].join(",")
        ),
      ].join("\n");
      filename = `monthly-analytics-${Date.now()}.csv`;
    }

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent)
    );
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    enqueueSnackbar("Analytics exported successfully", {
      variant: "success",
    });
  };

  // Calculate summary statistics
  const totalOrders = selectedCity
    ? (analyticsData.citySummary.find((c) => c.city === selectedCity)?.totalOrders ?? 0)
    : analyticsData.citySummary.reduce((sum, city) => sum + city.totalOrders, 0);
  const totalCities = analyticsData.citySummary.length;

  // Prepare chart data (sorted by totalOrders descending)
  const cityChartData = [...analyticsData.citySummary].sort(
    (a, b) => b.totalOrders - a.totalOrders
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Order Analytics
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchCitySummary}
              disabled={analyticsData.loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={analyticsData.loading || totalOrders === 0}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
        <Divider />
      </Box>

      {/* Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Date From"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Date To"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="city-select-label">City</InputLabel>
                <Select
                  labelId="city-select-label"
                  label="City"
                  value={selectedCity || ""}
                  onChange={(e) => {
                    const val = e.target.value as string;
                    setSelectedCity(val);
                    if (val) {
                      fetchCityClientAnalytics(val);
                      fetchCityMonthlyAnalytics(val);
                      fetchCityMonthlyClientAnalytics(val);
                    } else {
                      // clear selections
                      setClientAnalytics([]);
                      setCityMonthlyAnalytics([]);
                      setCityClientMonthlyData([]);
                      setCityClientSeries([]);
                    }
                  }}
                >
                  <MenuItem value="">All Cities</MenuItem>
                  {analyticsData.citySummary.map((s) => (
                    <MenuItem key={s.city} value={s.city}>
                      {s.city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={() => { fetchCitySummary(); fetchMonthlyOrders(); }}
                disabled={analyticsData.loading}
                sx={{ mr: 1 }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                disabled={analyticsData.loading}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Key Metrics Section */}
      {totalOrders > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography color="textSecondary" gutterBottom>
                  Total Orders
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {totalOrders.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography color="textSecondary" gutterBottom>
                  Cities Covered
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {totalCities}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Error State */}
      {analyticsData.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {analyticsData.error}
        </Alert>
      )}

      {/* Loading State */}
      {analyticsData.loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No Data State */}
      {!analyticsData.loading &&
        totalOrders === 0 &&
        !analyticsData.error && (
          <Alert severity="info">No analytics data available</Alert>
        )}

      {/* Tabs Section */}
      {totalOrders > 0 && !analyticsData.loading && (
        <Card>
          <CardHeader title="Analytics Dashboard" />
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                <Tab label="City-wise Analytics" />
                <Tab label="Monthly Analytics" />
              </Tabs>
            </Box>

            {/* City-wise Analytics Tab */}
            <TabPanel value={tabValue} index={0}>
              {/* Show global monthly chart when no city is selected */}
              {!selectedCity && monthlyChartData.length > 0 ? (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Orders Trend (Monthly)
                  </Typography>
                  <Card>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={360}>
                        <LineChart
                          data={monthlyChartData}
                          margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis dataKey="month" tick={{ fill: "#666", fontSize: 12 }} />
                          <YAxis tick={{ fill: "#666", fontSize: 12 }} />
                          <Tooltip contentStyle={{ backgroundColor: "rgba(255,255,255,0.95)", border: "2px solid #1976d2", borderRadius: 8 }} />
                          <Line type="monotone" dataKey="totalOrders" stroke={METRIC_COLORS.primary} name="Orders" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Box>
              ) : null}

              {/* Bar Chart (only show when no city filter is selected) */}
              {!selectedCity && (
                <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Orders by City (Bar Chart)
                </Typography>
                <Card>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={cityChartData}
                        margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="city"
                          angle={-45}
                          textAnchor="end"
                          height={100}
                        />
                        <YAxis
                          label={{
                            value: "Orders",
                            angle: -90,
                            position: "insideLeft",
                          }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="totalOrders"
                          fill="#8884d8"
                          name="Total Orders"
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                </Box>
              )}


                {/* Selected City - Client & Monthly Analytics */}
                {selectedCity ? (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {`Analytics for ${selectedCity}`}
                    </Typography>
                    <Grid container spacing={2}>
                      {/* <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Top Clients
                        </Typography>
                        {clientAnalytics.length === 0 ? (
                          <Alert severity="info">No client data for this city</Alert>
                        ) : (
                          <Card>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                  data={clientAnalytics.slice(0, 12)}
                                  margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="clientName" angle={-45} textAnchor="end" height={80} />
                                  <YAxis />
                                  <Tooltip content={<ClientStackedTooltip />} />
                                  <Bar dataKey="totalOrders" fill={METRIC_COLORS.success} name="Orders" />
                                </BarChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        )}
                      </Grid> */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Monthly Totals
                        </Typography>
                        {cityMonthlyAnalytics.length === 0 ? (
                          <Alert severity="info">No monthly data for this city</Alert>
                        ) : (
                          <Card>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={cityMonthlyChartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="month" />
                                  <YAxis />
                                  <Tooltip />
                                  <Line type="monotone" dataKey="totalOrders" stroke={METRIC_COLORS.primary} strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
                          Monthly Client-wise Orders (by Client)
                        </Typography>
                        {cityClientMonthlyData.length === 0 ? (
                          <Alert severity="info">No monthly client-wise data for this city</Alert>
                        ) : (
                          <Card>
                            <CardContent>
                              <ResponsiveContainer width="100%" height={420}>
                                <BarChart data={cityClientMonthlyChartData} margin={{ top: 20, right: 30, left: 0, bottom: 80 }}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="month" angle={-45} textAnchor="end" interval={0} height={80} />
                                  <YAxis />
                                  <Tooltip content={<ClientStackedTooltip />} />
                                  <Legend verticalAlign="top" />
                                  {cityClientSeries.map((client, idx) => {
                                    const hasValue = (cityClientMonthlyChartData || []).some((row: any) => Number(row[client] || 0) > 0);
                                    if (!hasValue) return null;
                                    const color = clientColorMap[client] || CLIENT_PALETTE[idx % CLIENT_PALETTE.length];
                                    return (
                                      <Bar key={client} dataKey={client} stackId="a" fill={color} name={client} />
                                    );
                                  })}
                                </BarChart>
                              </ResponsiveContainer>
                            </CardContent>
                          </Card>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                ) : null}

              {/* Detailed Table */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  City-wise Summary ({cityChartData.length} Cities)
                </Typography>
                <Card>
                  <CardContent>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead sx={{ }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                              Total Orders
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                              % of Total
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cityChartData.map((city) => (
                            <TableRow key={city.city}>
                              <TableCell>
                                <Chip
                                  label={city.city}
                                  variant="outlined"
                                  color="primary"
                                  clickable
                                  onClick={() => {
                                    setSelectedCity(city.city);
                                    fetchCityClientAnalytics(city.city);
                                    fetchCityMonthlyAnalytics(city.city);
                                    fetchCityMonthlyClientAnalytics(city.city);
                                    setTabValue(0);
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {city.totalOrders.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, color: "#666" }}
                                >
                                  {totalOrders > 0
                                    ? (
                                        ((city.totalOrders / totalOrders) * 100).toFixed(
                                          2
                                        )
                                      )
                                    : "0"}
                                  %
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* Total Row */}
                          <TableRow sx={{ }}>
                            <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {totalOrders.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              100%
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            </TabPanel>

            {/* Monthly Analytics Tab */}
            <TabPanel value={tabValue} index={1}>
              {monthlyChartData.length > 0 ? (
                <>
                  {/* Line Chart */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Orders Trend (Monthly)
                    </Typography>
                    <Card>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <LineChart
                            data={monthlyChartData}
                            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "rgba(255, 255, 255, 0.95)",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                              }}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="totalOrders"
                              stroke="#8884d8"
                              name="Total Orders"
                              strokeWidth={2}
                              dot={{ fill: "#8884d8", r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Box>

                  {/* Monthly Table */}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Monthly Summary
                    </Typography>
                    <Card>
                      <CardContent>
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">
                                  Total Orders
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {monthlyDataToShow.map((month) => (
                                <TableRow key={month.month}>
                                  <TableCell>
                                    <Chip
                                      label={month.month}
                                      variant="outlined"
                                      color="primary"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {month.totalOrders.toLocaleString()}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Box>
                </>
              ) : (
                <Alert severity="info">
                  Select a date range to view monthly analytics
                </Alert>
              )}
            </TabPanel>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default OrderAnalytics;
